import { Mic, Music2, Upload, Drum, Menu, Trash2, type LucideIcon } from "lucide-react";
import { ProcessingLimitDisplay } from "@/components/ProcessingLimitDisplay";
import { parseSongInfo } from "@/utils/songUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
  useSidebar,
} from "@/components/ui/sidebar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { isSessionExpired } from "@/utils/sessionExpiry";

interface SidebarNavigationProps {
  sessions: any[];
  acapellaSessions: any[];
  drumSessions: any[];
  activeSession: string | null;
  onSessionSelect: (sessionId: string | null, type?: "stem" | "acapella" | "drum") => void;
  onSessionDelete?: (sessionId: string, type: "stem" | "acapella" | "drum") => void;
  showStemSplitter?: boolean;
  showAcapella?: boolean;
  showDrumRemover?: boolean;
}

type SessionType = "stem" | "acapella" | "drum";

const SessionRow = ({
  session,
  type,
  isActive,
  onSelect,
  onDelete,
  Icon,
  onDeleteRequest,
}: {
  session: any;
  type: SessionType;
  isActive: boolean;
  onSelect: (id: string, type: SessionType) => void;
  onDelete?: (id: string, type: SessionType) => void;
  Icon: LucideIcon;
  onDeleteRequest: (id: string, type: SessionType) => void;
}) => {
  const expired = isSessionExpired(session);
  const [isHovered, setIsHovered] = useState(false);

  /*
   * The EXPIRED area is 58px wide and positioned 8px from the right.
   *
   * Delete button:
   *   right: 4px
   *   width: 32px
   *
   * Therefore the left edge of the delete button is 36px from
   * the right edge of the row.
   *
   * Relative to the 58px EXPIRED area, that boundary is about 52%.
   *
   * The mask is therefore COMPLETELY transparent from 52% onward.
   * Nothing from EXPIRED can remain visible underneath the button.
   */
  const expiredHoverMask =
    "linear-gradient(" +
    "to right," +
    "black 0%," +
    "black 24%," +
    "rgba(0,0,0,0.90) 29%," +
    "rgba(0,0,0,0.72) 34%," +
    "rgba(0,0,0,0.48) 39%," +
    "rgba(0,0,0,0.25) 44%," +
    "rgba(0,0,0,0.08) 49%," +
    "transparent 52%," +
    "transparent 100%" +
    ")";

  return (
    <SidebarMenuItem
      className="group/menu-item relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <SidebarMenuButton
        onClick={() => onSelect(session.id, type)}
        className={`w-full h-10 ${expired ? "!pr-[66px]" : "!pr-12"} ${
          isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
        }`}
      >
        <Icon className="w-4 h-4 shrink-0" />

        <span className="truncate min-w-0 flex-1">{parseSongInfo(session.original_filename).title}</span>
      </SidebarMenuButton>

      {expired && (
        <span
          className="
            pointer-events-none
            absolute
            inset-y-0
            right-2
            flex
            w-[58px]
            items-center
            justify-end
            whitespace-nowrap
            text-[10px]
            uppercase
            tracking-wide
            text-muted-foreground/70
          "
          style={
            isHovered && onDelete
              ? {
                  WebkitMaskImage: expiredHoverMask,
                  maskImage: expiredHoverMask,
                }
              : undefined
          }
        >
          Expired
        </span>
      )}

      {onDelete && (
        <SidebarMenuAction
          showOnHover
          onClick={(e) => {
            e.stopPropagation();
            onDeleteRequest(session.id, type);
          }}
          aria-label="Delete session"
          className="
            z-[2]
            !top-1/2
            -translate-y-1/2
            !right-1
            !w-8
            !h-8
            [&>svg]:!w-5
            [&>svg]:!h-5
            hover:bg-red-500
            hover:text-white
          "
        >
          <Trash2 />
        </SidebarMenuAction>
      )}
    </SidebarMenuItem>
  );
};

export const SidebarNavigation = ({
  sessions,
  acapellaSessions,
  drumSessions,
  activeSession,
  onSessionSelect,
  onSessionDelete,
  showStemSplitter = true,
  showAcapella = true,
  showDrumRemover = true,
}: SidebarNavigationProps) => {
  const isMobile = useIsMobile();
  const { open, toggleSidebar } = useSidebar();

  const [sessionToDelete, setSessionToDelete] = useState<{
    id: string;
    type: SessionType;
  } | null>(null);

  if (isMobile && !open) {
    return null;
  }

  const handleDeleteConfirm = () => {
    if (sessionToDelete && onSessionDelete) {
      onSessionDelete(sessionToDelete.id, sessionToDelete.type);
      setSessionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setSessionToDelete(null);
  };

  const requestDelete = (id: string, type: SessionType) => {
    setSessionToDelete({
      id,
      type,
    });
  };

  return (
    <>
      <Sidebar className="bg-sidebar border-none">
        <SidebarHeader className="p-4">
          {isMobile && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10" onClick={toggleSidebar}>
                    <Menu className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>

                <TooltipContent>{open ? "Close menu" : "Open menu"}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <div className="flex">
            <img
              src="/lovable-uploads/7c2cb694-9ce5-46b4-8e24-19579fce26cc.png"
              alt="AI Stem Splitter Logo"
              className="h-6 object-contain"
            />
          </div>
        </SidebarHeader>

        <SidebarContent className="flex flex-col h-full">
          <SidebarMenu className="flex-1 space-y-2 px-4">
            <Accordion type="multiple" defaultValue={["stems", "acapella", "drums"]} className="w-full">
              {showStemSplitter && (
                <AccordionItem value="stems" className="border-none">
                  <AccordionTrigger className="py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Music2 className="w-4 h-4" />

                      <span className="text-sm font-normal">AI Stem Splitter</span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pt-2">
                    <div className="flex flex-col space-y-1">
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => onSessionSelect(null, "stem")}
                          className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${
                            activeSession === null ? "bg-accent text-accent-foreground" : ""
                          }`}
                        >
                          <Upload className="w-4 h-4" />
                          <span>Upload audio</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>

                      {sessions?.map((session) => (
                        <SessionRow
                          key={session.id}
                          session={session}
                          type="stem"
                          isActive={session.id === activeSession}
                          onSelect={onSessionSelect}
                          onDelete={onSessionDelete}
                          Icon={Music2}
                          onDeleteRequest={requestDelete}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {showAcapella && (
                <AccordionItem value="acapella" className="border-none">
                  <AccordionTrigger className="py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Mic className="w-4 h-4" />

                      <span className="text-sm font-normal">Acapella Extractor</span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pt-2">
                    <div className="flex flex-col space-y-1">
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => onSessionSelect(null, "acapella")}
                          className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${
                            activeSession === null ? "bg-accent text-accent-foreground" : ""
                          }`}
                        >
                          <Upload className="w-4 h-4" />
                          <span>Upload audio</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>

                      {acapellaSessions?.map((session) => (
                        <SessionRow
                          key={session.id}
                          session={session}
                          type="acapella"
                          isActive={session.id === activeSession}
                          onSelect={onSessionSelect}
                          onDelete={onSessionDelete}
                          Icon={Mic}
                          onDeleteRequest={requestDelete}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {showDrumRemover && (
                <AccordionItem value="drums" className="border-none">
                  <AccordionTrigger className="py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Drum className="w-4 h-4" />

                      <span className="text-sm font-normal">Drum Remover</span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pt-2">
                    <div className="flex flex-col space-y-1">
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => onSessionSelect(null, "drum")}
                          className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${
                            activeSession === null ? "bg-accent text-accent-foreground" : ""
                          }`}
                        >
                          <Upload className="w-4 h-4" />
                          <span>Upload audio</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>

                      {drumSessions?.map((session) => (
                        <SessionRow
                          key={session.id}
                          session={session}
                          type="drum"
                          isActive={session.id === activeSession}
                          onSelect={onSessionSelect}
                          onDelete={onSessionDelete}
                          Icon={Drum}
                          onDeleteRequest={requestDelete}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </SidebarMenu>

          <ProcessingLimitDisplay />
        </SidebarContent>
      </Sidebar>

      <AlertDialog open={!!sessionToDelete} onOpenChange={() => !sessionToDelete || setSessionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>

            <AlertDialogDescription>
              This will permanently delete this audio file and all extracted stems. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>Cancel</AlertDialogCancel>

            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
